import pokemonSetMeta from "../data/pokemonSetMeta";
import { getPriceDollars } from "./price";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "avif"];
const POKEMON_SET_IMAGE_ALIASES = {
  "fire red leaf green": [
    "FireRed & LeafGreen",
    "firered & leafgreen",
    "Fire Red Leaf Green",
    "fire red leaf green",
    "FireRed LeafGreen",
    "firered leafgreen",
    "Fire Red & LeafGreen",
    "fire red & leafgreen",
    "Fire Red LeafGreen",
    "fire red leafgreen",
  ],
  "ruby and sapphire": [
    "Ruby and Sapphire",
    "ruby and sapphire",
    "Ruby & Sapphire",
    "ruby sapphire",
    "ruby & sapphire",
  ],
  "gym challenge": ["gym challenge"],
  "base set": ["base set", "baseset"],
  "crystal guardians": ["crystal guardian", "crystal guardians"],
  "team rocket returns": ["team rocket returns", "team rocket"],
  "pop series 1": ["Pop Series 1", "pop series 1", "Pop Series1", "pop series1", "pop1"],
  "pop series 2": ["Pop Series 2", "pop series 2", "Pop Series2", "pop series2", "pop2"],
  "pop series 3": ["Pop Series 3", "pop series 3", "Pop Series3", "pop series3", "pop3"],
  "pop series 4": ["Pop Series 4", "pop series 4", "Pop series 4", "pop series4", "pop4"],
};

const DEFAULT_THEME = {
  accent: "#255a8a",
  chipBg: "rgba(255,255,255,0.16)",
  heroGradient: "linear-gradient(135deg, #18304f 0%, #255a8a 58%, #f2c55b 100%)",
};

const THEME_BY_SLUG = {
  "chaos-rising": {
    accent: "#9f3b3b",
    chipBg: "rgba(255,255,255,0.16)",
    heroGradient: "linear-gradient(135deg, #38161a 0%, #9f3b3b 58%, #f0bf6a 100%)",
  },
  "perfect-order": {
    accent: "#1e5c52",
    chipBg: "rgba(255,255,255,0.16)",
    heroGradient: "linear-gradient(135deg, #15342f 0%, #1e5c52 58%, #dfc66b 100%)",
  },
  "ascended-heroes": {
    accent: "#6b2fb3",
    chipBg: "rgba(255,255,255,0.16)",
    heroGradient: "linear-gradient(135deg, #281244 0%, #6b2fb3 55%, #f38f4f 100%)",
  },
  "black-bolt": {
    accent: "#20242f",
    chipBg: "rgba(255,255,255,0.18)",
    heroGradient: "linear-gradient(135deg, #12161e 0%, #2a3140 55%, #9aa6bf 100%)",
  },
  "white-flare": {
    accent: "#8b6b2f",
    chipBg: "rgba(255,255,255,0.2)",
    heroGradient: "linear-gradient(135deg, #4f4d45 0%, #a48a59 58%, #efe6c5 100%)",
  },
  "pokemon-151": {
    accent: "#c43d52",
    chipBg: "rgba(255,255,255,0.16)",
    heroGradient: "linear-gradient(135deg, #562030 0%, #c43d52 58%, #f1d67a 100%)",
  },
  "prismatic-evolutions": {
    accent: "#5b4fd1",
    chipBg: "rgba(255,255,255,0.16)",
    heroGradient: "linear-gradient(135deg, #1f2554 0%, #5b4fd1 55%, #f4c36d 100%)",
  },
};

export function pokemonSetSlugFromName(name) {
  return String(name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function normalizePokemonSetName(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizePokemonSetNameForImage(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ");
}

function buildPokemonSetPublicImagePath(fileName) {
  const normalizedFileName = String(fileName || "").trim().replace(/^\/+/, "");
  if (!normalizedFileName) return "";
  return encodeURI(`/sets/pokemon/${normalizedFileName}`);
}

export function isEnergySubset(entry) {
  const normalizedName = normalizePokemonSetName(entry?.set_name);
  if (!normalizedName) return false;
  return normalizedName.endsWith(" energy");
}

export function isExcludedPokemonSet(entry) {
  const normalizedName = normalizePokemonSetName(entry?.set_name);
  const normalizedCode = String(entry?.code || "").trim().toUpperCase();
  if (normalizedCode === "KSS") return true;
  if (normalizedCode === "BOG") return true;
  if (normalizedName === "kalos starter set") return true;
  if (normalizedName === "best of game") return true;
  if (normalizedName === "southern islands") return true;
  return isEnergySubset(entry);
}

function buildImageNameVariants(value) {
  const raw = normalizePokemonSetNameForImage(value);
  if (!raw) return [];

  const lower = raw.toLowerCase();
  const collapsed = lower.replace(/\s+/g, " ").trim();
  const noPunctuation = collapsed.replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
  const andVersion = noPunctuation.replace(/&/g, "and").replace(/\s+/g, " ").trim();
  const noAmpersand = noPunctuation.replace(/&/g, " ").replace(/\s+/g, " ").trim();
  const compact = andVersion.replace(/\s+/g, "");
  const slug = pokemonSetSlugFromName(raw);
  const slugSpaced = slug.replace(/-/g, " ").trim();

  return Array.from(new Set([
    raw,
    lower,
    collapsed,
    noPunctuation,
    andVersion,
    noAmpersand,
    compact,
    slug,
    slug.toLowerCase(),
    slugSpaced,
  ].filter(Boolean)));
}

export function getPokemonSetImageAliases(entry) {
  const normalizedName = normalizePokemonSetName(entry?.set_name);
  return POKEMON_SET_IMAGE_ALIASES[normalizedName] || [];
}

export function getPokemonSetImageCandidates(entry) {
  const candidates = [];
  const literalSetName = String(entry?.set_name || "").trim();
  const explicitImageFile = String(entry?.imageFile || "").trim();
  if (entry?.promo) {
    for (const ext of IMAGE_EXTENSIONS) {
      candidates.push(`/sets/pokemon/promo.${ext}`);
      candidates.push(`/sets/pokemon/promos.${ext}`);
    }
  }

  if (explicitImageFile) {
    candidates.push(buildPokemonSetPublicImagePath(explicitImageFile));
  }

  const baseCandidates = Array.from(new Set([
    String(entry?.code || "").trim(),
    String(entry?.code || "").trim().toLowerCase(),
    literalSetName,
    ...getPokemonSetImageAliases(entry),
    ...(Array.isArray(entry?.aliases) ? entry.aliases : []),
    ...(Array.isArray(entry?.matchTerms) ? entry.matchTerms : []),
    String(entry?.slug || "").trim(),
  ].filter(Boolean)));

  for (const ext of IMAGE_EXTENSIONS) {
    candidates.push(`/sets/pokemon/${literalSetName}.${ext}`);
  }

  const nameCandidates = Array.from(new Set(
    baseCandidates.flatMap((value) => buildImageNameVariants(value))
  ));

  for (const base of nameCandidates) {
    for (const ext of IMAGE_EXTENSIONS) {
      candidates.push(`/sets/pokemon/${base}.${ext}`);
    }
  }
  return Array.from(new Set(candidates));
}

export function getPokemonSetTheme(entry) {
  if (entry?.promo) {
    return {
      accent: "#8f5d1f",
      chipBg: "rgba(255,255,255,0.18)",
      heroGradient: "linear-gradient(135deg, #3b2410 0%, #8f5d1f 55%, #f1d27d 100%)",
    };
  }
  return THEME_BY_SLUG[entry?.slug] || DEFAULT_THEME;
}

const normalizedSets = [];
const bySlug = new Map();
const bySlugAlias = new Map();

Object.entries(pokemonSetMeta).forEach(([code, meta], index) => {
  const registryCode = meta?.code === null ? "" : code;
  const setName = String(meta?.set_name || "").trim();
  const slug = String(meta?.slug || pokemonSetSlugFromName(setName)).trim().toLowerCase();
  const existing = bySlug.get(slug);

  if (existing) {
    if (registryCode) existing.aliasCodes.push(registryCode);
    return;
  }

  const aliases = Array.isArray(meta?.aliases) ? meta.aliases.map((value) => String(value || "").trim()).filter(Boolean) : [];
  const matchTerms = Array.isArray(meta?.matchTerms) ? meta.matchTerms.map((value) => String(value || "").trim()).filter(Boolean) : [];
  const entry = {
    ...meta,
    code: registryCode,
    aliasCodes: [],
    aliases,
    matchTerms,
    slug,
    set_name: setName,
    displayOrder: index,
  };
  bySlug.set(slug, entry);
  for (const alias of [...aliases, ...matchTerms]) {
    const aliasSlug = pokemonSetSlugFromName(alias);
    if (!aliasSlug || aliasSlug === slug || bySlug.has(aliasSlug) || bySlugAlias.has(aliasSlug)) continue;
    bySlugAlias.set(aliasSlug, entry);
  }
  normalizedSets.push(entry);
});

export const pokemonSets = normalizedSets;

export const browsablePokemonSets = normalizedSets.filter((entry) => !isExcludedPokemonSet(entry));

export function getPokemonSetBySlug(slug) {
  const normalizedSlug = String(slug || "").trim().toLowerCase();
  return bySlug.get(normalizedSlug) || bySlugAlias.get(normalizedSlug) || null;
}

export function sortPokemonSetItems(items, sortBy) {
  return [...items]
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const a = left.item;
      const b = right.item;

      if (sortBy === "name_asc") {
        return String(a?.name || "").localeCompare(String(b?.name || "")) || left.index - right.index;
      }
      if (sortBy === "name_desc") {
        return String(b?.name || "").localeCompare(String(a?.name || "")) || left.index - right.index;
      }
      if (sortBy === "price_desc") {
        return (getPriceDollars(b) ?? -Infinity) - (getPriceDollars(a) ?? -Infinity) || left.index - right.index;
      }
      if (sortBy === "price_asc") {
        return (getPriceDollars(a) ?? Infinity) - (getPriceDollars(b) ?? Infinity) || left.index - right.index;
      }
      if (sortBy === "item_id_asc") {
        return Number(a?.item_id || 0) - Number(b?.item_id || 0) || left.index - right.index;
      }

      return Number(b?.item_id || 0) - Number(a?.item_id || 0) || left.index - right.index;
    })
    .map(({ item }) => item);
}
